import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { ApiServerService } from '../../core/api/api-server.service';
import { ElasticFirestoreBase } from '../../core/db/elastic-firestore-base';
import { ElasticResult, ElasticSearchBody } from '../../core/db/elastic.models';
import { Product, ProductType } from './product.models';

/**
 * Product Firestore + ElasticSearch data-access service.
 * Extends {@link ElasticFirestoreBase} for CRUD and full-text search capabilities.
 */
@Injectable({ providedIn: 'root' })
export class ProductDbService extends ElasticFirestoreBase<Product> {

  constructor(
    firestore: Firestore,
    apiServerService: ApiServerService,
  ) {
    super('products', firestore, apiServerService);
  }

  // ─── ElasticSearch ───────────────────────────────────────────────────────────

  /** Paginated ES search with dynamic filters, sorting and nested-field support. */
  searchProducts(
    paginator: { pageIndex: number; pageSize: number; },
    sort: { active: string; direction: string; },
    filters: Record<string, any[]>,
    extras: Record<string, { type?: string; nested?: string; }> = {},
  ): Promise<ElasticResult<Product>> {
    const from = paginator.pageIndex * paginator.pageSize;
    const size = paginator.pageSize;

    const boolQuery: any = { must: [], filter: [], must_not: [] };
    Object.keys(filters).forEach(key => {
      const type = extras?.[key]?.type ?? 'term';
      const queryTerm: any = { must: [], filter: [], must_not: [] };

      filters[key].forEach((f: any) => {
        const values = Array.isArray(f.value) ? f.value : [f.value];
        const queryOption = f.op === '!==' ? 'must_not' : 'must';
        values.forEach((value: any) => {
          switch (type) {
            case 'id':
              queryTerm[queryOption].push({ ids: { values: [typeof value === 'object' ? value.id : value] } });
              break;
            case 'range':
              queryTerm[queryOption].push({ range: { [f.key]: value } });
              break;
            case 'search':
              const fields = (value.terms || []).map((t: any) => t.score ? `${t.field}^${t.score}` : t.field);
              queryTerm[queryOption].push({
                multi_match: { query: value.value, fields, type: 'most_fields', fuzziness: 'AUTO' },
              });
              break;
            default:
              queryTerm[queryOption].push({ term: { [f.key]: typeof value === 'object' ? value.id : value } });
              break;
          }
        });
      });

      Object.keys(queryTerm).forEach(k => {
        if (queryTerm[k].length > 1) {
          queryTerm[k] = [{ bool: { should: queryTerm[k], minimum_should_match: 1 } }];
        }
      });

      if (extras?.[key]?.nested) {
        Object.keys(queryTerm).forEach(k => {
          if (queryTerm[k].length) {
            queryTerm[k] = [{
              nested: { path: extras[key].nested, query: { bool: { filter: queryTerm[k] } } },
            }];
          }
        });
      }

      Object.keys(boolQuery).forEach(k => boolQuery[k].push(...queryTerm[k]));
    });

    let esSort: any = {};
    if (sort.active && sort.direction?.length) {
      const index = sort.active;
      esSort = [
        {
          ...(index !== 'relevance' && { [index]: { order: sort.direction } }),
          ...(index === 'relevance' && {
            _script: {
              type: 'number',
              script: {
                lang: 'painless',
                source: "if(doc['relevance'].value < 64) {return 0} else {return 1}",
                params: { factor: 1.1 },
              },
              order: 'desc',
            },
            _score: { order: 'desc' },
            relevance: { order: 'desc' },
          }),
        },
      ];
    }

    const body: ElasticSearchBody = {
      query: { bool: boolQuery },
      from,
      size,
      sort: esSort,
    };
    return this.search(body);
  }

  /** Searches documents (print) or products (store) via ES with optional featured/vendor/status filters. */
  searchDocumentsList(opts: {
    type: 'document' | 'product';
    featured?: boolean;
    vendor?: string;
    status?: string;
    excludeProduct?: string;
    size?: number;
  }): Promise<ElasticResult<Product>> {
    const filter: any[] = [];
    const mustNot: any[] = [];

    if (opts.type === 'document') {
      filter.push({ term: { type: ProductType.PRINT } });
      mustNot.push({ term: { internal: true } });
    }
    if (opts.type === 'product') {
      filter.push({ term: { type: ProductType.STORE } });
    }
    if (opts.featured !== undefined) {
      filter.push({ term: { featured: opts.featured } });
    }
    if (opts.vendor) {
      filter.push({ term: { 'vendor.id': opts.vendor } });
    }
    if (opts.status) {
      filter.push({ term: { status: opts.status } });
    }
    if (opts.excludeProduct) {
      mustNot.push({ ids: { values: [opts.excludeProduct] } });
    }

    return this.search({
      query: {
        bool: {
          filter,
          ...(mustNot.length && { must_not: mustNot }),
        },
      },
      size: opts.size ?? 6,
      sort: [{ 'title.key': 'asc' }],
    });
  }
}

